<template>
  <div class="employee-management">
    <div class="card-header inv-log-card-header">
      <div class="card-title">出库日志统计</div>
      <div class="inv-log-card-header__actions inv-mgmt-excel-actions">
        <el-upload
          class="el-upload el-upload--button inv-mgmt-upload"
          :show-file-list="false"
          :before-upload="handleImportExcel"
          accept=".xls,.xlsx"
        >
          <el-button type="default" size="small" class="inv-mgmt-excel-btn">导入Excel</el-button>
        </el-upload>
        <el-button type="default" size="small" class="inv-mgmt-excel-btn" @click="exportExcel">导出Excel</el-button>
        <a class="inv-mgmt-template-link" href="javascript:;" @click.prevent="handleDownloadTemplate">下载模板</a>
      </div>
    </div>

    <el-card shadow="never" class="employee-card">
      <el-form
        :inline="false"
        :model="filters"
        label-position="top"
        class="inventory-filter-form inventory-filter-form--stacked"
      >
        <!-- 第 1 行：使用区域/操作人/关键词 -->
        <div class="filter-row filter-row-1">
          <el-form-item label="使用区域：">
            <el-select v-model="filters.region" placeholder="全部" size="small" style="width: 150px;">
              <el-option label="全部" value="all" />
              <el-option
                v-for="r in regionOptions"
                :key="r"
                :label="r"
                :value="r"
              />
            </el-select>
          </el-form-item>

          <el-form-item label="操作人：">
            <el-input
              v-model="filters.operator"
              placeholder="请输入操作人"
              size="small"
              clearable
              style="width: 180px;"
            />
          </el-form-item>

          <el-form-item label="关键词：">
            <el-input
              v-model="filters.keyword"
              placeholder="商品名称/备注"
              size="small"
              clearable
              prefix-icon="el-icon-search"
              style="width: 220px;"
            />
          </el-form-item>
        </div>

        <!-- 第 2 行：时间 -->
        <div class="filter-row filter-row-2">
          <el-form-item label="时间：">
            <el-date-picker
              v-model="dateRange"
              type="daterange"
              range-separator="至"
              start-placeholder="开始日期"
              end-placeholder="结束日期"
              align="right"
              size="small"
              style="width: 240px;"
            />
          </el-form-item>
        </div>

        <!-- 底部按钮：筛选/重置（右对齐） -->
        <div class="filter-actions">
          <el-button
            type="primary"
            size="mini"
            class="el-button el-button--primary el-button--mini"
            icon="el-icon-search"
            @click="search"
          >
            筛选
          </el-button>
          <el-button
            type="primary"
            size="mini"
            class="el-button el-button--primary el-button--mini"
            icon="el-icon-refresh"
            @click="reset"
          >
            重置
          </el-button>
        </div>
      </el-form>
    </el-card>

    <el-card shadow="never" class="employee-card" style="margin-top: 18px;">
      <div class="card-header" style="margin-bottom: 12px;">
        <div class="card-title">出库日志列表</div>
      </div>

      <el-table :data="list" border stripe style="width: 100%;" class="employee-table" @sort-change="handleSortChange">
        <el-table-column prop="operationTime" label="操作时间" min-width="190" sortable="custom" />
        <el-table-column prop="logTypeLabel" label="日志类型" min-width="150" sortable="custom" />
        <el-table-column prop="productName" label="商品名称" min-width="160" sortable="custom" />
        <el-table-column prop="quantityDisplay" label="数量" min-width="120" sortable="custom" />
        <el-table-column prop="usageArea" label="使用区域" min-width="120" sortable="custom" class-name="inv-col-nowrap" />
        <el-table-column prop="operator" label="操作人" min-width="120" sortable="custom" />
        <el-table-column prop="remark" label="备注" min-width="160" sortable="custom" />
      </el-table>

      <div class="bottom-summary">
        <div class="bottom-summary-item">
          <div class="bottom-summary-label">总数量</div>
          <div class="bottom-summary-value">{{ summary.totalQuantity }}</div>
        </div>
        <div class="bottom-summary-item">
          <div class="bottom-summary-label">操作次数</div>
          <div class="bottom-summary-value">{{ summary.operationCount }}</div>
        </div>
      </div>

      <div class="pagination">
        <el-pagination
          background
          layout="prev, pager, next"
          :total="total"
          :page-size="pageSize"
          :current-page="currentPage"
          @current-change="handleCurrentChange"
        />
      </div>
    </el-card>
  </div>
</template>

<script>
import {
  fetchInventoryRegions,
  fetchInventoryLogs,
  downloadInventoryTemplate,
  importInventoryExcel
} from '../api/inventory'
import { INVENTORY_USAGE_AREAS } from '../constants/inventory'
import { getUserInfo } from '../utils/auth'

export default {
  name: 'InventoryOutboundLogs',
  data() {
    const userInfo = getUserInfo()
    return {
      regions: [],
      advancedOpen: true,
      sortProp: '',
      sortOrder: '',
      filters: {
        region: 'all',
        operator: userInfo && userInfo.username ? userInfo.username : '',
        keyword: ''
      },
      dateRange: [],
      list: [],
      total: 0,
      pageSize: 10,
      currentPage: 1,
      summary: { totalQuantity: 0, operationCount: 0 }
    }
  },
  computed: {
    regionOptions() {
      const set = new Set([...INVENTORY_USAGE_AREAS, ...(this.regions || [])])
      return Array.from(set)
    }
  },
  mounted() {
    this.init()
  },
  methods: {
    async init() {
      const regionsResp = await fetchInventoryRegions()
      this.regions = regionsResp && regionsResp.data ? regionsResp.data : []

      await this.fetchLogs()
    },

    getRangePayload() {
      if (!this.dateRange || !this.dateRange.length) return {}
      return {
        startDate: this.dateRange[0] ? this.dateRange[0].toISOString() : '',
        endDate: this.dateRange[1] ? this.dateRange[1].toISOString() : ''
      }
    },

    async fetchLogs() {
      const rangePayload = this.getRangePayload()
      try {
        const resp = await fetchInventoryLogs({
          type: 'out',
          region: this.filters.region,
          category: 'all',
          operator: this.filters.operator,
          keyword: this.filters.keyword,
          ...rangePayload,
          page: this.currentPage,
          pageSize: this.pageSize
        })

        if (resp && resp.code === 200 && resp.data) {
          this.list = resp.data.list || []
          this.total = resp.data.total || 0
          this.summary = resp.data.summary || this.summary
        } else {
          this.list = []
          this.total = 0
        }
      } catch (e) {
        this.list = []
        this.total = 0
        this.summary = { totalQuantity: 0, operationCount: 0 }
      }

      this.sortList()
    },

    async search() {
      this.currentPage = 1
      await this.fetchLogs()
    },

    async reset() {
      const userInfo = getUserInfo()
      const operator = userInfo && userInfo.username ? userInfo.username : ''
      this.filters = { region: 'all', operator, keyword: '' }
      this.dateRange = []
      this.currentPage = 1
      await this.fetchLogs()
    },

    handleCurrentChange(val) {
      this.currentPage = val
      this.fetchLogs()
    },

    async handleDownloadTemplate() {
      try {
        await downloadInventoryTemplate()
      } catch (e) {
        this.$message.error('模板下载失败')
      }
    },

    async exportExcel() {
      try {
        const header = ['操作时间', '日志类型', '商品名称', '数量', '使用区域', '操作人', '备注']
        const escapeCsv = (s) => {
          const str = String(s !== undefined && s !== null ? s : '')
          return `"${str.replace(/"/g, '""')}"`
        }
        const rows = (this.list || []).map((r) => [
          r.operationTime,
          r.logTypeLabel,
          r.productName,
          r.quantityDisplay != null ? r.quantityDisplay : r.quantity,
          r.usageArea || r.region,
          r.operator,
          r.remark
        ])
        const csv = [header.map(escapeCsv).join(','), ...rows.map((x) => x.map(escapeCsv).join(','))].join('\n')
        const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' })
        const url = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = '出库日志统计_导出.csv'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        URL.revokeObjectURL(url)
      } catch (e) {
        this.$message.error('导出失败')
      }
    },

    formatImportErrors(result) {
      const errs = result && result.errors ? result.errors : []
      if (!errs.length) return ''
      return errs.slice(0, 12).map((x) => `第 ${x.line} 行：${x.message}`).join('\n')
    },

    async handleImportExcel(file) {
      try {
        const userInfo = getUserInfo()
        const op = userInfo && userInfo.username ? userInfo.username : '管理员'
        const resp = await importInventoryExcel(file, op)
        if (resp && resp.code === 200) {
          const extra = this.formatImportErrors(resp.data)
          if (extra) {
            this.$alert(`${resp.message || '导入完成'}\n\n${extra}`, '导入结果', { type: 'warning' })
          } else {
            this.$message.success(resp.message || '导入成功')
          }
          await this.fetchLogs()
        } else if (resp && resp.code === 400 && resp.data && resp.data.errors) {
          this.$alert(this.formatImportErrors(resp.data) || resp.message || '导入失败', '导入校验失败', { type: 'error' })
        } else {
          this.$message.error((resp && resp.message) || '导入失败')
        }
      } catch (e) {
        const data = e && e.response && e.response.data
        if (data && data.data && data.data.errors) {
          this.$alert(this.formatImportErrors(data.data) || data.message, '导入校验失败', { type: 'error' })
        } else {
          this.$message.error((data && data.message) || '导入失败')
        }
      }
      return false
    },

    sortList() {
      if (!this.sortProp || !this.sortOrder) return
      const prop = this.sortProp
      const orderFactor = this.sortOrder === 'ascending' ? 1 : -1

      const sorted = (this.list || []).slice().sort((a, b) => {
        const va = a ? a[prop] : undefined
        const vb = b ? b[prop] : undefined

        if (va === undefined || va === null) return 1
        if (vb === undefined || vb === null) return -1

        const numA = typeof va === 'number' ? va : Number(va)
        const numB = typeof vb === 'number' ? vb : Number(vb)
        const bothNumeric = Number.isFinite(numA) && Number.isFinite(numB)

        const left = bothNumeric ? numA : String(va)
        const right = bothNumeric ? numB : String(vb)

        if (left > right) return 1 * orderFactor
        if (left < right) return -1 * orderFactor
        return 0
      })

      this.list = sorted
    },

    handleSortChange({ prop, order }) {
      this.sortProp = prop
      this.sortOrder = order
      this.sortList()
    }
  }
}
</script>

<style scoped>
.inv-log-card-header {
  flex-wrap: wrap;
}

.inv-log-card-header__actions {
  flex: 1;
  display: flex;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 8px;
  min-width: 220px;
}

.employee-management > .card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
}

.filter-topbar {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 12px;
}

.inventory-filter-form {
  width: 100%;
  overflow-x: auto;
}

.filter-row {
  display: flex;
  flex-wrap: nowrap;
  gap: 16px;
}

.filter-row-1 {
  width: 100%;
  display: grid;
  grid-template-columns: repeat(3, minmax(150px, 1fr));
  align-items: start;
  column-gap: 20px;
}

.filter-row-2 {
  width: 100%;
  margin-top: 16px;
}

.inventory-filter-form ::v-deep .el-form-item {
  margin-bottom: 0;
}

.inventory-filter-form.inventory-filter-form--stacked ::v-deep .el-form-item {
  margin-bottom: 12px;
}

.inventory-filter-form ::v-deep .el-form-item__label {
  white-space: nowrap;
  line-height: 1.3;
  padding-bottom: 4px;
}

.inventory-filter-form ::v-deep .el-form-item__content {
  margin-left: 0 !important;
}

.filter-actions {
  margin-top: 14px;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}

.bottom-summary {
  margin-top: 20px;
  display: flex;
  gap: 14px;
}

.bottom-summary-item {
  flex: 1;
  background: var(--card-bg);
  border-radius: 12px;
  box-shadow: var(--shadow-soft);
  padding: 14px 12px;
}

.bottom-summary-label {
  font-size: 12px;
  color: #4e5969;
  margin-bottom: 8px;
}

.bottom-summary-value {
  font-size: 22px;
  font-weight: 800;
  color: #1d2129;
}

.pagination {
  margin-top: 14px;
  display: flex;
  justify-content: flex-end;
}
</style>

